import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import {
  ArrowLeft,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  FileCode2,
} from "lucide-react";

export default function ParserAdmin() {
  const [consulenti, setConsulenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api
      .get("/consulenti")
      .then((r) => setConsulenti(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Errore"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const showToast = (text, ok = true) => {
    setToast({ text, type: ok ? "ok" : "err" });
    setTimeout(() => setToast(null), 4000);
  };

  const ok = consulenti.filter((c) => c.parser_status === "ok").length;
  const missing = consulenti.length - ok;

  return (
    <div data-testid="parsers-admin-page">
      <Header />
      {toast && (
        <div
          data-testid="parsers-toast"
          style={{
            position: "fixed",
            top: 78,
            right: 24,
            background: toast.type === "ok" ? "var(--pa-green-bg)" : "var(--pa-red-bg)",
            color: toast.type === "ok" ? "var(--pa-green)" : "var(--pa-red)",
            border: `1px solid ${toast.type === "ok" ? "#CDE5BA" : "#EFC7C7"}`,
            padding: "10px 14px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            zIndex: 40,
            boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
            maxWidth: 420,
          }}
        >
          {toast.text}
        </div>
      )}
      <div className="pa-container" style={{ padding: "28px 24px 64px" }}>
        <button
          onClick={() => nav("/")}
          style={{
            background: "transparent",
            border: "none",
            color: "var(--pa-muted)",
            fontSize: 13,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            padding: 0,
            marginBottom: 18,
          }}
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={14} /> Dashboard
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 700,
                color: "var(--pa-navy)",
                margin: 0,
                letterSpacing: "-0.01em",
              }}
            >
              Parser dei consulenti
            </h1>
            <p
              style={{
                color: "var(--pa-muted)",
                fontSize: 14,
                marginTop: 4,
                maxWidth: 700,
              }}
            >
              Carica il file Python (.py) del parser per ogni consulente. Una volta
              caricato, i clienti collegati potranno generare l'Excel direttamente
              dal portale.
            </p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div className="pa-stat" data-testid="stat-ok">
              <span className="pa-stat-label">Parser configurati</span>
              <span className="pa-stat-value" style={{ color: "var(--pa-green)" }}>
                {ok}
              </span>
            </div>
            <div className="pa-stat" data-testid="stat-missing">
              <span className="pa-stat-label">Da configurare</span>
              <span className="pa-stat-value" style={{ color: "var(--pa-amber)" }}>
                {missing}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              background: "var(--pa-red-bg)",
              color: "var(--pa-red)",
              padding: 12,
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        <div className="pa-card" style={{ overflow: "auto" }} data-testid="parsers-table-wrap">
          <table className="pa-table" data-testid="parsers-table">
            <thead>
              <tr>
                <th style={{ paddingLeft: 18 }}>Consulente</th>
                <th>Sede</th>
                <th>Stato</th>
                <th>File parser</th>
                <th style={{ textAlign: "right", paddingRight: 18 }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} style={{ padding: 40, textAlign: "center", color: "var(--pa-muted)" }}>
                    Caricamento…
                  </td>
                </tr>
              )}
              {!loading &&
                consulenti.map((c) => (
                  <ConsulenteRow
                    key={c.id}
                    consulente={c}
                    onChanged={(msg, ok2) => {
                      showToast(msg, ok2);
                      load();
                    }}
                  />
                ))}
            </tbody>
          </table>
        </div>

        <div
          className="pa-card"
          style={{
            padding: 16,
            marginTop: 18,
            background: "#F7F9FB",
            fontSize: 13,
            color: "var(--pa-muted)",
            lineHeight: 1.55,
          }}
        >
          <strong style={{ color: "var(--pa-navy)" }}>Requisiti del file parser</strong>
          <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
            <li>Estensione <code>.py</code>, codifica UTF-8.</li>
            <li>
              Deve definire una funzione <code>main()</code> che accetti{" "}
              <code>argparse</code> con argomenti <code>pdfs...</code> posizionali e{" "}
              <code>-o DIR</code> per la directory di output.
            </li>
            <li>
              Deve produrre un file Excel <code>.xlsx</code> con un foglio chiamato{" "}
              <code>Scrittura</code> con le colonne Data · Dare · Avere · Descrizione · Dettaglio.
            </li>
            <li>
              Il nome del file (es. <code>parser_imola.py</code>) diventa il
              <em> modulo</em> registrato per il consulente.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function ConsulenteRow({ consulente, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.endsWith(".py")) {
      onChanged("È richiesto un file .py", false);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("consulente_id", consulente.id);
      fd.append("file", f);
      const r = await api.post("/parsers/upload", fd);
      onChanged(`✓ Parser '${r.data.parser_module}.py' caricato per ${consulente.nome}.`, true);
    } catch (e) {
      onChanged(e?.response?.data?.detail || "Caricamento fallito.", false);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const detach = async () => {
    if (!window.confirm(`Rimuovere il parser per ${consulente.nome}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/parsers/${consulente.id}`);
      onChanged(`Parser rimosso per ${consulente.nome}.`, true);
    } catch (e) {
      onChanged(e?.response?.data?.detail || "Operazione fallita.", false);
    } finally {
      setBusy(false);
    }
  };

  const isOk = consulente.parser_status === "ok";

  return (
    <tr data-testid={`consulente-row-${consulente.id}`}>
      <td style={{ paddingLeft: 18 }}>
        <div style={{ fontWeight: 600, color: "var(--pa-text)" }}>{consulente.nome}</div>
        <div style={{ fontSize: 11, color: "var(--pa-muted)", marginTop: 2 }}>
          id: {consulente.id}
        </div>
      </td>
      <td style={{ color: "var(--pa-muted)", fontSize: 13 }}>
        {consulente.sede || "—"}
      </td>
      <td>
        {isOk ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: "var(--pa-green-bg)",
              color: "var(--pa-green)",
            }}
            data-testid={`status-${consulente.id}`}
          >
            <CheckCircle2 size={13} /> Configurato
          </span>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: "var(--pa-amber-bg)",
              color: "var(--pa-amber)",
            }}
            data-testid={`status-${consulente.id}`}
          >
            <AlertTriangle size={13} /> Da configurare
          </span>
        )}
      </td>
      <td>
        {isOk ? (
          <span
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 12,
              color: "var(--pa-text)",
              padding: "2px 8px",
              background: "#F1F2F4",
              borderRadius: 4,
            }}
          >
            {consulente.parser_module}.py
          </span>
        ) : (
          <span style={{ color: "var(--pa-muted)", fontSize: 13 }}>—</span>
        )}
      </td>
      <td style={{ textAlign: "right", paddingRight: 18 }}>
        <input
          ref={inputRef}
          type="file"
          accept=".py,text/x-python"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`file-input-${consulente.id}`}
        />
        <div style={{ display: "inline-flex", gap: 6 }}>
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            data-testid={`upload-${consulente.id}`}
            className={isOk ? "" : "pa-btn-primary"}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              border: isOk ? "1px solid var(--pa-border)" : "1px solid var(--pa-navy)",
              background: isOk ? "transparent" : "var(--pa-navy)",
              color: isOk ? "var(--pa-navy)" : "white",
              cursor: busy ? "wait" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            {busy ? <span className="pa-spinner" /> : isOk ? <FileCode2 size={13} /> : <Upload size={13} />}
            {isOk ? "Sostituisci" : "Carica .py"}
          </button>
          {isOk && (
            <button
              onClick={detach}
              disabled={busy}
              data-testid={`detach-${consulente.id}`}
              title="Scollega parser"
              style={{
                padding: "6px 8px",
                borderRadius: 8,
                fontSize: 12,
                background: "transparent",
                border: "1px solid var(--pa-border)",
                color: "var(--pa-red)",
                cursor: "pointer",
              }}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
